import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ExportModalComponent, ExportOptions, ExportColumn } from '../../../shared/components/export-modal/export-modal.component';
import { DateFormatPipe, StatusBadgePipe } from '../../../shared/pipes';
import { ApiError } from '../../../abstract/api-error';
import { FieldService } from '../../../core/data-services/field.service';
import { OrganizationService } from '../../../core/data-services/organization.service';
import { SprayRecordService } from '../../../core/data-services/spray-record.service';
import { LogbookOverviewService } from '../../../core/api-services/logbook-overview.service';
import { OrganizationPilot } from '../../../core/response-models/organization-pilot.model';
import { SprayRecordListItem } from '../../../core/response-models/spray-record-list-item.model';

/** List filters, sent to the hub. `fieldId` / `pilotId` are ids. */
interface FilterState {
  status: string;
  fieldId: string;
  pilotId: string;
  dateFrom: string;
  dateTo: string;
}

const PAGE_SIZE = 25;
const NO_FILTERS: FilterState = { status: '', fieldId: '', pilotId: '', dateFrom: '', dateTo: '' };

@Component({
  selector: 'app-spray-records',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ConfirmModalComponent, ExportModalComponent, DateFormatPipe, StatusBadgePipe],
  templateUrl: './spray-records.component.html',
  styleUrls: ['./spray-records.component.scss']
})
export class SprayRecordsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;
  @ViewChild('exportModal') exportModal!: ExportModalComponent;

  private sprayRecordService = inject(SprayRecordService);
  private fieldService = inject(FieldService);
  private organizationService = inject(OrganizationService);
  private logbookOverviewService = inject(LogbookOverviewService);

  // Signal-based state
  private filtersSignal = signal<FilterState>({ ...NO_FILTERS });
  private pageSignal = signal(1);

  // Computed signals from service
  readonly isLoading = this.sprayRecordService.isLoading;

  /** The loaded page, already filtered by the hub. */
  readonly sprayRecords = computed(() => this.sprayRecordService.getRecordsAsListItems());
  /** Kept for the template: the hub applies the filters, so this is the loaded page. */
  readonly filteredRecords = this.sprayRecords;

  // Paging
  readonly currentPage = computed(() => this.sprayRecordService.paging()?.page ?? this.pageSignal());
  readonly totalPages = computed(() => this.sprayRecordService.paging()?.totalPages ?? 1);
  readonly totalRecords = computed(() => this.sprayRecordService.paging()?.totalItems ?? this.sprayRecords().length);

  // Organization-wide stats (not just the loaded page)
  readonly completedCount = signal(0);
  readonly pendingCount = signal(0);
  readonly totalAcreage = computed(() => this.logbookOverviewService.stats$()?.totalAcresSprayed ?? 0);

  // Filter dropdown options
  readonly fieldOptions = computed(() => this.fieldService.fieldOptions());
  readonly pilotOptions = signal<OrganizationPilot[]>([]);

  // UI state (local signals)
  isFilterOpen = signal(false);
  activeFilterCount = signal(0);

  // For delete confirmation
  recordToDelete: SprayRecordListItem | null = null;

  // Export columns configuration
  exportColumns: ExportColumn[] = [
    { key: 'date', label: 'Date', selected: true },
    { key: 'fieldName', label: 'Field Name', selected: true },
    { key: 'cropType', label: 'Crop Type', selected: true },
    { key: 'acreage', label: 'Acreage', selected: true },
    { key: 'chemicalName', label: 'Chemical', selected: true },
    { key: 'applicationRate', label: 'Application Rate', selected: true },
    { key: 'pilotName', label: 'Pilot', selected: true },
    { key: 'droneId', label: 'Drone ID', selected: true },
    { key: 'status', label: 'Status', selected: true },
    { key: 'weatherConditions', label: 'Weather', selected: false },
    { key: 'notes', label: 'Notes', selected: false }
  ];

  private dateFormatPipe = new DateFormatPipe();

  // Expose filters for template reading
  get filters(): FilterState {
    return this.filtersSignal();
  }

  constructor(private router: Router) {}

  // Update a single filter field and re-trigger computed signals
  updateFilter(key: keyof FilterState, value: string): void {
    this.filtersSignal.update(current => ({ ...current, [key]: value ?? '' }));
    this.updateActiveFilterCount();
    this.goToPage(1);
  }

  ngOnInit(): void {
    this.goToPage(1);
    void this.loadStats();
    void this.loadPilots();
  }

  // Paging
  goToPage(page: number): void {
    this.pageSignal.set(page);
    const filters = this.filtersSignal();
    void this.sprayRecordService.loadRecords({
      status: filters.status,
      fieldId: filters.fieldId,
      pilotId: filters.pilotId,
      startDate: filters.dateFrom,
      endDate: filters.dateTo,
      page,
      limit: PAGE_SIZE
    });
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.goToPage(this.currentPage() + 1);
    }
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.goToPage(this.currentPage() - 1);
    }
  }

  /** Status counts across all records, and the logbook's total acres. Failures keep the last values. */
  private async loadStats(): Promise<void> {
    try {
      const [completed, pending] = await Promise.all([
        this.sprayRecordService.countRecords('completed'),
        this.sprayRecordService.countRecords('pending'),
        this.logbookOverviewService.loadStats()
      ]);
      this.completedCount.set(completed);
      this.pendingCount.set(pending);
    } catch (error: unknown) {
      console.error('[SprayRecords] Error loading stats:', ApiError.fromHttpError(error).message);
    }
  }

  private async loadPilots(): Promise<void> {
    try {
      this.pilotOptions.set(await this.organizationService.getMyOrganizationPilots());
    } catch (error: unknown) {
      console.error('[SprayRecords] Error loading pilots:', ApiError.fromHttpError(error).message);
    }
  }

  // Filter methods
  toggleFilter(): void {
    this.isFilterOpen.update(open => !open);
  }

  updateActiveFilterCount(): void {
    const filters = this.filtersSignal();
    let count = 0;
    if (filters.status) count++;
    if (filters.fieldId) count++;
    if (filters.pilotId) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    this.activeFilterCount.set(count);
  }

  clearFilters(): void {
    this.filtersSignal.set({ ...NO_FILTERS });
    this.activeFilterCount.set(0);
    this.goToPage(1);
  }

  // Export methods
  openExportModal(): void {
    const count = this.filteredRecords().length;
    this.exportModal.open({
      title: 'Export Spray Records',
      description: `Export ${count} spray record${count !== 1 ? 's' : ''} based on current filters.`,
      formats: ['csv', 'pdf', 'excel'],
      columns: this.exportColumns,
      showDateRange: true,
      showColumnSelection: true
    });
  }

  onExport(options: ExportOptions): void {
    console.log('Export requested with options:', options);
    // TODO: Implement actual export logic
    const count = this.filteredRecords().length;
    alert(`Exporting ${count} records as ${options.format.toUpperCase()}`);
  }

  addSprayRecord(): void {
    this.router.navigate(['/logbook/spray-records/add']);
  }

  viewRecord(record: SprayRecordListItem): void {
    this.router.navigate(['/logbook/spray-records', record.id, 'view']);
  }

  editRecord(record: SprayRecordListItem): void {
    this.router.navigate(['/logbook/spray-records', record.id, 'edit']);
  }

  deleteRecord(record: SprayRecordListItem): void {
    this.recordToDelete = record;
    this.confirmModal.open({
      title: 'Delete Spray Record',
      message: `Are you sure you want to delete the spray record for "${record.fieldName}" on ${this.dateFormatPipe.transform(record.date)}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  onDeleteConfirmed(): void {
    if (this.recordToDelete) {
      void this.deleteAndRefresh(this.recordToDelete.id);
      this.recordToDelete = null;
    }
  }

  /** The service reloads the page; the counts and acres are refreshed here. */
  private async deleteAndRefresh(id: string): Promise<void> {
    if (await this.sprayRecordService.deleteRecord(id)) {
      // Deleting the last row of a page leaves it empty; step back one.
      if (this.sprayRecords().length === 0 && this.currentPage() > 1) {
        this.goToPage(this.currentPage() - 1);
      }
      await this.loadStats();
    }
  }

  onDeleteCancelled(): void {
    this.recordToDelete = null;
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }
}
